import { enableBodyScroll, disableBodyScroll } from 'body-scroll-lock-upgrade';
import * as focusTrap from 'focus-trap';
import { gsap, Power2 } from 'gsap/all';

class ShareOverlay {
  constructor() {
    this.rootEl = document.querySelector('.js-share-overlay');
    if (!this.rootEl) {
      throw new Error('Root share tools element not found.');
    }

    this.triggerEl = document.querySelector('.js-share-trigger');
    if (!this.triggerEl) {
      throw new Error('Trigger share tools element not found.');
    }

    this.closeButtonEl = this.rootEl.querySelector('.js-share-close');
    this.lineEl = this.rootEl.querySelector('.js-share-line');
    this.shareListEl = this.rootEl.querySelector('.share-overlay__list');
    this.shareTextCircle = this.rootEl.querySelector('.share-overlay__text-circle');
    this.shareLinks = this.rootEl.querySelectorAll('.share-overlay__link');

    this.isOpen = false;
    this.focusTrap = focusTrap.createFocusTrap(this.rootEl, {
      escapeDeactivates: false,
      setReturnFocus: this.triggerEl,
    });

    this.init();
  }

  init() {
    // Toggle overlay on trigger button click
    this.triggerEl.addEventListener('click', () => {
      this.isOpen = !this.isOpen;
      this.update();
    });

    // Close the overlay on close button click
    this.closeButtonEl.addEventListener('click', () => {
      this.isOpen = false;
      this.update();
    });

    // Close the overlay on escape
    document.addEventListener('keydown', evt => {
      if (this.isOpen && evt.key === 'Escape') {
        evt.preventDefault();
        this.isOpen = false;
        this.update();
      }
    });

    // Animate the line to the next link on hover or focus
    this.shareLinks.forEach(link => {
      const onLinkHover = () => {
        this.positionLine(link);
        this.animateLineIn();
      };
      link.addEventListener('mouseover', onLinkHover);
      link.addEventListener('focusin', onLinkHover);
    });

    // Re-animate to the focused element on hover out, or animate the line out
    // if no link has focus
    const onListHoverExit = () => {
      requestAnimationFrame(() => {
        if (this.shareListEl.contains(document.activeElement)) {
          this.positionLine(document.activeElement);
          this.animateLineIn();
        } else if (window.innerWidth > 768) {
          this.animateLineOut();
        }
      });
    };
    this.shareListEl.addEventListener('mouseleave', onListHoverExit);
    this.shareListEl.addEventListener('focusout', onListHoverExit);
  }

  close() {
    if (this.isOpen) {
      this.isOpen = false;
      this.update();
    }
  }

  openAnimation(onComplete) {
    gsap.to(this.rootEl, {
      duration: 0.3,
      opacity: 1,
      ease: Power2.easeInOut,
      onComplete: onComplete,
    });
  }

  closeAnimation() {
    gsap.to(this.rootEl, {
      duration: 0.3,
      opacity: 0,
      ease: Power2.easeInOut,
    });
  }

  animateLineIn() {
    const hasReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const duration = hasReducedMotion ? 0 : 0.3;

    const circleRect = this.shareTextCircle.getBoundingClientRect();
    gsap.to('line', {
      strokeDashoffset: this.getCirclePosition(circleRect) === 'left' ? 2000 : 0,
      duration,
      ease: Power2.easeInOut,
    });
  }

  animateLineOut() {
    const hasReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const duration = hasReducedMotion ? 0 : 0.3;

    gsap.to('line', {
      strokeDashoffset: 1000,
      duration,
      ease: Power2.easeInOut,
      onComplete: () => {
        // Reset line
        this.lineEl.removeAttribute('style');
      },
    });
  }

  getCirclePosition(circleRect) {
    if (circleRect.left < window.innerWidth - circleRect.right) {
      return 'left';
    } else {
      return 'right';
    }
  }

  positionLine(el) {
    const adminOffset = document.querySelector('.admin-bar') ? 32 : 0;
    const elRect = el.getBoundingClientRect();
    const circleRect = this.shareTextCircle.getBoundingClientRect();
    const line = this.lineEl.querySelector('line');

    if (this.getCirclePosition(circleRect) === 'left') {
      line.setAttribute('y2', circleRect.top - adminOffset + circleRect.height / 2);
      line.setAttribute('y1', elRect.top - adminOffset + elRect.height / 2);

      this.lineEl.style.cssText = `
        left: ${circleRect.right}px;
        width: ${elRect.left - circleRect.right - 20}px;
        height: calc(100% - ${circleRect.top});
      `;
    } else {
      line.setAttribute('y1', circleRect.top - adminOffset + circleRect.height / 2);
      line.setAttribute('y2', elRect.top - adminOffset + elRect.height / 2);

      this.lineEl.style.cssText = `
        right: ${window.innerWidth - circleRect.left}px;
        width: ${circleRect.left - elRect.right - 20}px;
        height: calc(100% - ${circleRect.top});
      `;
    }
  }

  positionShareCircle() {
    const shareEl = this.triggerEl.getBoundingClientRect();

    requestAnimationFrame(() => {
      this.shareTextCircle.style.cssText = `
        top: calc(${shareEl.top}px - var(--wp-admin--admin-bar--height, 0px));
        left: ${shareEl.left}px;
      `;
    });
  }

  update() {
    this.triggerEl.setAttribute('aria-expanded', this.isOpen);
    this.rootEl.setAttribute('aria-hidden', !this.isOpen);

    if (this.isOpen) {
      disableBodyScroll(this.rootEl);
      this.positionObserver = new ResizeObserver(() => {
        this.positionShareCircle();
      });
      this.positionObserver.observe(document.body);
      this.openAnimation(() => {
        this.focusTrap.activate();
      });
    } else {
      enableBodyScroll(this.rootEl);
      this.closeAnimation();
      this.focusTrap.deactivate();
      this.positionObserver.disconnect();
      this.positionObserver = null;
    }
  }
}

export default ShareOverlay;
